'use client';

/**
 * React context and provider for analytics.
 *
 * Wrap your app in <AnalyticsProvider> to make useAnalytics(),
 * useErrorTracker(), and useFeatureFlag() available.
 */

import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import type { IAnalyticsProvider, UserTraits, GroupTraits } from '../interfaces/IAnalyticsProvider';
import type { IErrorTracker } from '../interfaces/IErrorTracker';
import type { IFeatureFlags } from '../interfaces/IFeatureFlags';
import type { AnalyticsEvent } from '../events/types';

interface AnalyticsContextValue {
    provider: IAnalyticsProvider;
    errorTracker: IErrorTracker | null;
    featureFlags: IFeatureFlags | null;
}

const AnalyticsReactContext = createContext<AnalyticsContextValue | null>(null);

export interface AnalyticsProviderProps {
    children: ReactNode;
    /** Product analytics provider (PostHog, GA4, Datadog, Composite, Noop) */
    provider: IAnalyticsProvider;
    /** Error tracking provider (Sentry, Datadog, Noop). Optional. */
    errorTracker?: IErrorTracker;
    /** Feature flags provider (PostHog, Noop). Optional. */
    featureFlags?: IFeatureFlags;
    /** Called after provider initialization */
    onReady?: () => void;
}

export function AnalyticsProvider({
    children,
    provider,
    errorTracker,
    featureFlags,
    onReady,
}: AnalyticsProviderProps) {
    const initialized = useRef(false);

    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        // Providers with async init expose an initialize() method
        const providerAny = provider as any;
        if (typeof providerAny.initialize === 'function') {
            providerAny.initialize().then(() => onReady?.()).catch((err: unknown) => {
                console.error('[analytics] Failed to initialize provider:', err);
            });
        } else {
            onReady?.();
        }
    }, [provider, onReady]);

    return (
        <AnalyticsReactContext.Provider
            value={{
                provider,
                errorTracker: errorTracker ?? null,
                featureFlags: featureFlags ?? null,
            }}
        >
            {children}
        </AnalyticsReactContext.Provider>
    );
}

// ─── Hooks ──────────────────────────────────────────────────────────

function useAnalyticsContext(): AnalyticsContextValue {
    const ctx = useContext(AnalyticsReactContext);
    if (!ctx) {
        throw new Error('useAnalytics must be used within <AnalyticsProvider>');
    }
    return ctx;
}

/**
 * Primary analytics hook. Track events, identify users, log pages.
 */
export function useAnalytics() {
    const { provider } = useAnalyticsContext();

    return {
        /**
         * Track a typed event from the event catalog.
         * Usage: analytics.trackEvent(orderingEvents.orderPlaced({ ... }))
         */
        trackEvent<TName extends string, TProps extends Record<string, unknown>>(
            event: AnalyticsEvent<TName, TProps>,
        ) {
            return provider.track(event.name, event.properties);
        },

        /** Track a raw event by name. Prefer trackEvent() with typed events. */
        track(event: string, properties?: Record<string, unknown>) {
            return provider.track(event, properties);
        },

        /** Identify a user (call on login) */
        identify(userId: string, traits?: UserTraits) {
            return provider.identify(userId, traits);
        },

        /** Track a page view (call on route change) */
        page(name: string, properties?: Record<string, unknown>) {
            return provider.page(name, properties);
        },

        /** Associate user with a restaurant/tenant group */
        group(groupId: string, groupType: string, traits?: GroupTraits) {
            provider.group(groupId, groupType, traits);
        },

        /** Reset identity on logout */
        reset() {
            provider.reset();
        },

        /** GDPR opt-out */
        setOptOut(optOut: boolean) {
            provider.setOptOut(optOut);
        },

        /** Whether the provider is ready */
        isConfigured: provider.isConfigured(),

        /** Provider name (for debugging) */
        providerName: provider.providerName,
    };
}

/**
 * Optional analytics hook. Same surface as {@link useAnalytics}, but safe to
 * call OUTSIDE an `<AnalyticsProvider>` — it returns a no-op shim instead of
 * throwing. Use this in SHARED package components (e.g. `@order-up/client`)
 * that are rendered by some apps with a provider (ordering, restaurant,
 * checkout) and some without (website), so a single component can emit funnel
 * events where a provider exists and stay inert where one doesn't.
 *
 * Reads the context unconditionally (always one `useContext` call, never a
 * conditional hook), then branches on whether a provider is present — so it is
 * safe under the rules of hooks.
 */
export function useOptionalAnalytics(): ReturnType<typeof useAnalytics> {
    const ctx = useContext(AnalyticsReactContext);
    const provider = ctx?.provider ?? null;

    if (!provider) {
        return {
            trackEvent: () => ({ success: false as const, error: 'No analytics provider' }),
            track: () => ({ success: false as const, error: 'No analytics provider' }),
            identify: () => ({ success: false as const, error: 'No analytics provider' }),
            page: () => ({ success: false as const, error: 'No analytics provider' }),
            group: () => {},
            reset: () => {},
            setOptOut: () => {},
            isConfigured: false,
            providerName: 'None',
        };
    }

    return {
        trackEvent: (event) => provider.track(event.name, event.properties),
        track: (event, properties) => provider.track(event, properties),
        identify: (userId, traits) => provider.identify(userId, traits),
        page: (name, properties) => provider.page(name, properties),
        group: (groupId, groupType, traits) => provider.group(groupId, groupType, traits),
        reset: () => provider.reset(),
        setOptOut: (optOut) => provider.setOptOut(optOut),
        isConfigured: provider.isConfigured(),
        providerName: provider.providerName,
    };
}

/**
 * Error tracking hook. Capture exceptions and messages.
 */
export function useErrorTracker() {
    const { errorTracker } = useAnalyticsContext();

    if (!errorTracker) {
        return {
            captureException: (_error: Error | unknown) => ({ success: false as const, error: 'No tracker configured' }),
            captureMessage: (_message: string) => ({ success: false as const, error: 'No tracker configured' }),
            addBreadcrumb: (_b: { category: string; message: string }) => {},
            isConfigured: false,
            providerName: 'None',
        };
    }

    return {
        captureException: errorTracker.captureException.bind(errorTracker),
        captureMessage: errorTracker.captureMessage.bind(errorTracker),
        addBreadcrumb: errorTracker.addBreadcrumb.bind(errorTracker),
        isConfigured: errorTracker.isConfigured(),
        providerName: errorTracker.providerName,
    };
}

/**
 * Feature flag hook. Check flags and variants.
 */
export function useFeatureFlag(flag: string, defaultValue = false): boolean {
    const { featureFlags } = useAnalyticsContext();
    return featureFlags?.isEnabled(flag, defaultValue) ?? defaultValue;
}

/**
 * Feature flag variant hook. Get multivariate flag values.
 */
export function useFeatureFlagVariant<T = string>(flag: string, defaultValue?: T): T {
    const { featureFlags } = useAnalyticsContext();
    return featureFlags?.getVariant<T>(flag, defaultValue) ?? (defaultValue as T);
}
